import cloneDeep from 'lodash/cloneDeep'
import get from 'lodash/get'
import set from 'lodash/set'
import { useState } from 'react'
import { getRequirementId, isRequirementRef, toRequirementRef } from '../../../../../util/directoryRoleRequirement.util'
import { INPUT_POLICY_PATH, isRoleInput } from '../../policy_feature/inputs'

const deriveInitialState = (policyBody) => {
  const autoModeInputs = []
  const requirementIds = {}

  Object.keys(INPUT_POLICY_PATH)
    .filter(isRoleInput)
    .forEach((input) => {
      const values = get(policyBody, INPUT_POLICY_PATH[input]) || []
      const references = values.filter(isRequirementRef).map(getRequirementId)
      if (references.length) {
        autoModeInputs.push(input)
        requirementIds[input] = references
      }
    })

  return { autoModeInputs, requirementIds }
}

export const getAutoModeRoleInputs = (policyBody) => deriveInitialState(policyBody).autoModeInputs

// Owns whether include/exclude roles are resolved from custom directory role requirements
// (matched by name at deploy) or still prompted. Built-in role template ids are not refs, so
// editing the attached requirements keeps any built-in ids already on the field.
export default function useRoleFieldRequirements({ initialPolicyBody, setPolicyBody }) {
  const [{ autoModeInputs, requirementIds }, setState] = useState(() => deriveInitialState(initialPolicyBody))

  const isAutoMode = (input) => autoModeInputs.includes(input)
  const getAttachedRequirementIds = (input) => requirementIds[input] || []

  // Builds on React's pending policy body (functional setState) rather than a render-time
  // snapshot, so clearing include and exclude roles in the same pass keeps both edits.
  const writeFieldValue = (input, buildValues) => {
    setPolicyBody((previousPolicyBody) => {
      const updated = cloneDeep(previousPolicyBody) || {}
      set(updated, INPUT_POLICY_PATH[input], buildValues(get(previousPolicyBody, INPUT_POLICY_PATH[input]) || []))
      return updated
    })
  }

  const toggleAutoMode = (input, enabled) => {
    setState((current) => ({
      autoModeInputs: enabled
        ? [...current.autoModeInputs, input]
        : current.autoModeInputs.filter((key) => key !== input),
      requirementIds: enabled ? current.requirementIds : { ...current.requirementIds, [input]: [] },
    }))

    if (!enabled) {
      writeFieldValue(input, () => [])
    }
  }

  const setAttachedRequirementIds = (input, updatedIds) => {
    setState((current) => ({ ...current, requirementIds: { ...current.requirementIds, [input]: updatedIds } }))
    writeFieldValue(input, (currentValues) => [
      ...currentValues.filter((value) => !isRequirementRef(value)),
      ...updatedIds.map(toRequirementRef),
    ])
  }

  const hydrate = (nextPolicyBody) => {
    setState(deriveInitialState(nextPolicyBody))
  }

  return { isAutoMode, getAttachedRequirementIds, toggleAutoMode, setAttachedRequirementIds, hydrate }
}
